import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { 
  DollarSign,
  Users,
  Target,
  TrendingUp,
  LayoutGrid,
  Globe,
  Zap,
  Calendar,
  Filter,
  Download,
  ChevronRight,
  AlertTriangle,
  Lightbulb,
  ArrowUp,
  ArrowDown,
  Bell,
  Settings,
  PieChart,
  Activity,
  TrendingDown,
  MousePointerClick
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Update platformData date labels
const last30Days = Array.from({length: 30}, (_, i) => {
  const date = new Date();
  date.setDate(date.getDate() - (29 - i));
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
});

const platformData = {
  spend: {
    google: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (20000 - 12000) + 12000)),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (15000 - 8000) + 8000)),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    meta: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (12000 - 8000) + 8000)),
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (10000 - 6000) + 6000)),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    tiktok: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (10000 - 6000) + 6000)),
          borderColor: 'rgb(147, 51, 234)',
          backgroundColor: 'rgba(147, 51, 234, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (8000 - 4000) + 4000)),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    }
  },
  roas: {
    google: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.random() * (0.5 - 0.2) + 0.2),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.random() * (0.4 - 0.1) + 0.1),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    meta: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.random() * (0.4 - 0.1) + 0.1),
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.random() * (0.3 - 0.05) + 0.05),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    tiktok: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.random() * (0.3 - 0.1) + 0.1),
          borderColor: 'rgb(147, 51, 234)',
          backgroundColor: 'rgba(147, 51, 234, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.random() * (0.2 - 0.05) + 0.05),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    }
  },
  conversions: {
    google: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (300 - 200) + 200)),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (250 - 150) + 150)),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    meta: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (200 - 100) + 100)),
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (150 - 50) + 50)),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    tiktok: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (150 - 50) + 50)),
          borderColor: 'rgb(147, 51, 234)',
          backgroundColor: 'rgba(147, 51, 234, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.floor(Math.random() * (100 - 30) + 30)),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    }
  },
  cpc: {
    google: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.random() * (0.8 - 0.7) + 0.7),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.random() * (0.8 - 0.7) + 0.7),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    meta: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.random() * (0.7 - 0.6) + 0.6),
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.random() * (0.7 - 0.6) + 0.6),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
    fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    },
    tiktok: {
      labels: last30Days,
      datasets: [
        {
          label: 'Current',
          data: Array.from({length: 30}, () => Math.random() * (0.7 - 0.6) + 0.6),
          borderColor: 'rgb(147, 51, 234)',
          backgroundColor: 'rgba(147, 51, 234, 0.1)',
    fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: 'Previous',
          data: Array.from({length: 30}, () => Math.random() * (0.7 - 0.6) + 0.6),
          borderColor: 'rgb(209, 213, 219)',
          backgroundColor: 'rgba(209, 213, 219, 0.1)',
    fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    }
  }
};

// Mevcut formatCurrency fonksiyonunun hemen üstüne formatNumber fonksiyonunu ekleyelim
const formatNumber = (value) => {
  // Null veya undefined kontrolü
  if (value === null || value === undefined) return '0';
  
  // Sayısal değere çevir
  const num = Number(value);
  
  // Sayısal formatı düzenle
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  } else {
    return num.toFixed(0);
  }
};

// Para birimi formatlamak için fonksiyon
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currencyDisplay: 'code',
  }).format(value).replace('TRY', '').trim();
};

// formatCurrency fonksiyonunun hemen altına şu fonksiyonu ekleyelim
const formatCurrencyWhole = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    // TRY sembolünü kaldıralım
    currencyDisplay: 'code',
  }).format(value).replace('TRY', '').trim();
};

export default function AdsOverview() {
  const [dateRange, setDateRange] = useState('30days');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['google', 'meta', 'tiktok']);
  const [selectedMetric, setSelectedMetric] = useState('spend');
  
  // AuthContext yerine useAuth hook'unu kullanalım
  const { auth, selectedBrand } = useAuth();
  
  // Unified daily summary verisi için state
  const [isLoading, setIsLoading] = useState(false);

  // Unified Ad Platform verileri için state ekleyelim
  const [unifiedAdPlatformData, setUnifiedAdPlatformData] = useState(null);
  const [unifiedAdPlatformLoading, setUnifiedAdPlatformLoading] = useState(false);

  // Platform bazında toplam harcama verilerini tutan state ekleyelim
  const [platformSpendData, setPlatformSpendData] = useState({
    totalSpend: 0,
    platforms: []
  });

  // Platform bazında toplam tıklama verilerini tutan state ekleyelim
  const [platformClicksData, setPlatformClicksData] = useState({
    totalClicks: 0,
    platforms: []
  });

  // Platform bazında toplam dönüşüm verilerini tutan state ekleyelim
  const [platformConversionsData, setPlatformConversionsData] = useState({
    totalConversions: 0,
    platforms: []
  });

  // Platform bazında toplam ROAS verilerini tutan state ekleyelim
  const [platformRoasData, setPlatformRoasData] = useState({
    totalRevenue: 0,
    platforms: []
  });

  const dateRanges = [
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7days', label: '7 Days' },
    { id: '30days', label: '30 Days', default: true },
    { id: 'this_month', label: 'This Month' },
  ];

  const handleCustomDateChange = (dates) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
    if (start && end) {
      setDateRange('custom');
      setIsDateDropdownOpen(false);
      // API'yi yeniden çağır
      console.log('Custom date range selected:', start, end);
    }
  };

  const togglePlatform = (platform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  const fetchUnifiedAdPlatformData = useCallback(async () => {
    try {
      console.log(`===== OVERVIEW PAGE: FETCHING ALL UNIFIED AD PLATFORM DAILY SUMMARY DATA =====`);
      setUnifiedAdPlatformLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      
      // API endpoint'ini çağıralım - custom date desteği ekleyelim
      let apiUrl = `https://api.glowytics.com/api/google-ads/unified-ad-platform-daily-summary?dateRange=${dateRange}`;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const startDateStr = customStartDate.toISOString().split('T')[0];
        const endDateStr = customEndDate.toISOString().split('T')[0];
        apiUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
      }
      
      const response = await fetch(apiUrl, {
        credentials: 'include',
          headers: {
          'X-Brand-ID': brandId
        }
        });
        
        if (!response.ok) {
        throw new Error('Failed to fetch unified ad platform data');
      }

      const result = await response.json();
      
      // Sonuçları detaylı bir şekilde konsolda gösterelim
      console.log('OVERVIEW - Unified Ad Platform ALL Data - Raw API Response:', result);
      console.log('OVERVIEW - Data Type:', typeof result);
      console.log('OVERVIEW - Is Array:', Array.isArray(result));
      console.log('OVERVIEW - Total Record Count:', result.length);
      
      // Veriyi state'e kaydedelim
      setUnifiedAdPlatformData(result);
      
      // İlk ve son tarih aralığını belirleyelim
      if (result.length > 0) {
        // Tarihleri düzgün bir şekilde sıralayalım
        const sortedDates = [...new Set(result.map(item => new Date(item.date).getTime()))]
          .sort((a, b) => a - b)
          .map(timestamp => new Date(timestamp));
        
        const firstDate = sortedDates[0];
        const lastDate = sortedDates[sortedDates.length - 1];
        
        console.log(`OVERVIEW - Data Date Range: ${firstDate.toISOString().split('T')[0]} to ${lastDate.toISOString().split('T')[0]}`);
        console.log(`OVERVIEW - Total Days: ${Math.floor((lastDate - firstDate) / (1000 * 60 * 60 * 24)) + 1}`);
        
        // Şimdi veriyi frontend'de UI içinde gösterilecek tarih aralığına göre filtreleyebiliriz
        const filteredData = dateRange === 'all' 
          ? result 
          : filterDataByDateRange(result, dateRange);
        
        console.log(`OVERVIEW - Filtered Data for UI (${dateRange}):`, filteredData.length);
        
        // Benzersiz reklam kaynaklarını listeleyelim (Google Ads, Meta, vb.)
        const sources = [...new Set(filteredData.map(item => item.source))];
        console.log('OVERVIEW - Unique Ad Sources:', sources);
        
        // Tarih bazında gruplayalım
        const groupedByDate = filteredData.reduce((acc, item) => {
          const date = new Date(item.date).toISOString().split('T')[0];
          if (!acc[date]) acc[date] = [];
          acc[date].push(item);
          return acc;
        }, {});
        
        console.log('OVERVIEW - Data grouped by date:', groupedByDate);
        
        // Kaynak bazında toplam değerleri hesaplayalım
        const totalsBySource = {};
        filteredData.forEach(item => {
          if (!totalsBySource[item.source]) {
            totalsBySource[item.source] = {
              source: item.source,
              totalImpressions: 0,
              totalClicks: 0,
              totalCost: 0,
              totalConversions: 0,
              totalConversionValue: 0
            };
          }
          
          totalsBySource[item.source].totalImpressions += item.totalImpressions;
          totalsBySource[item.source].totalClicks += item.totalClicks;
          totalsBySource[item.source].totalCost += item.totalCost;
          totalsBySource[item.source].totalConversions += item.totalConversions;
          totalsBySource[item.source].totalConversionValue += item.totalConversionValue;
        });
        
        console.log('OVERVIEW - Totals by Source:', totalsBySource);
      }
      
      console.log(`===== OVERVIEW PAGE: UNIFIED AD PLATFORM ALL DATA FETCHED SUCCESSFULLY =====`);
      
    } catch (error) {
      console.error(`OVERVIEW - Error fetching unified ad platform data:`, error);
      toast.error('Failed to load unified ad platform data in Overview');
    } finally {
      setUnifiedAdPlatformLoading(false);
    }
  }, [dateRange, customStartDate, customEndDate]); // dateRange ve custom date değişikliklerinde API'yi yeniden çağır

  // Veriyi frontend'de filtrelemek için helper fonksiyon
  const filterDataByDateRange = (data, range) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let startDate;
    
    switch(range) {
      case 'yesterday':
        startDate = yesterday;
        break;
      case '7days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        break;
      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'custom':
        // Custom date için startDate ve endDate kullan
        if (customStartDate && customEndDate) {
          const customStart = new Date(customStartDate);
          customStart.setHours(0, 0, 0, 0);
          const customEnd = new Date(customEndDate);
          customEnd.setHours(23, 59, 59, 999);
          
          console.log('Filtering data for custom range:', customStart.toISOString(), 'to', customEnd.toISOString());
          
          const filtered = data.filter(item => {
            const itemDate = new Date(item.date);
            const inRange = itemDate >= customStart && itemDate <= customEnd;
            if (data.indexOf(item) < 5) { // Log first 5 items for debugging
              console.log('Item date:', item.date, 'In range:', inRange);
            }
            return inRange;
          });
          
          console.log('Custom filter result:', filtered.length, 'items');
          return filtered;
        }
        console.log('Custom date range selected but dates not set, returning all data');
        return data;
      default:
        // 'all' veya diğer değerler için tüm verileri döndür
        return data;
    }
    
    return data.filter(item => {
      const itemDate = new Date(item.date);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate >= startDate && itemDate <= today;
    });
  };

  // useEffect ile date range değişimlerini takip edelim
  useEffect(() => {
    console.log('useEffect triggered - fetching data for dateRange:', dateRange);
    if (dateRange === 'custom') {
      console.log('Custom date range:', customStartDate, customEndDate);
    }
    fetchUnifiedAdPlatformData();
  }, [fetchUnifiedAdPlatformData]);

  // Unified Ad Platform verilerini işleyip platform kırılımlı verileri hazırlayan fonksiyon
  useEffect(() => {
    // Unified Ad Platform verileri varsa işleyelim
    if (unifiedAdPlatformData && unifiedAdPlatformData.length > 0) {
      console.log('Processing unified ad platform data for dateRange:', dateRange);
      console.log('Total raw data records:', unifiedAdPlatformData.length);
      
      // Geçerli tarih aralığına göre verileri filtreleyelim
      const filteredData = filterDataByDateRange(unifiedAdPlatformData, dateRange);
      console.log('Filtered data records:', filteredData.length);
      
      // Platform bazında verileri gruplayalım
      const platformData = {};
      let totalSpend = 0;
      let totalClicks = 0;
      let totalConversions = 0;
      let totalRevenue = 0; // Toplam gelir
      
      filteredData.forEach(item => {
        const sourceName = item.source || 'Unknown';
        
        if (!platformData[sourceName]) {
          platformData[sourceName] = {
            name: sourceName,
            spend: 0,
            clicks: 0,
            impressions: 0,
            conversions: 0,
            conversionValue: 0,
            change: 0 // Değişim oranı için başlangıç değeri
          };
        }
        
        platformData[sourceName].spend += item.totalCost;
        platformData[sourceName].clicks += item.totalClicks;
        platformData[sourceName].impressions += item.totalImpressions;
        platformData[sourceName].conversions += item.totalConversions;
        platformData[sourceName].conversionValue += item.totalConversionValue;
        
        totalSpend += item.totalCost;
        totalClicks += item.totalClicks;
        totalConversions += item.totalConversions;
        totalRevenue += item.totalConversionValue;
      });
      
      // Platform verilerini işle
      const processedPlatforms = Object.values(platformData).map(platform => {
        return {
          ...platform,
          percentage: (platform.spend / totalSpend) * 100,
          clickPercentage: (platform.clicks / totalClicks) * 100,
          conversionPercentage: (platform.conversions / totalConversions) * 100,
          revenuePercentage: (platform.conversionValue / totalRevenue) * 100,
          // CPC, CPA ve ROAS hesaplamaları
          cpc: platform.clicks > 0 ? platform.spend / platform.clicks : 0,
          cpa: platform.conversions > 0 ? platform.spend / platform.conversions : 0,
          roas: platform.spend > 0 ? platform.conversionValue / platform.spend : 0,
          // Gerçek veriye geçene kadar sahte değişim değerleri
          change: platform.name.toLowerCase().includes('google') ? 12.3 : 
                  platform.name.toLowerCase().includes('meta') ? 8.7 : 32.5,
          clickChange: platform.name.toLowerCase().includes('google') ? 15.8 : 
                    platform.name.toLowerCase().includes('meta') ? 10.2 : 27.9,
          conversionChange: platform.name.toLowerCase().includes('google') ? 18.3 : 
                  platform.name.toLowerCase().includes('meta') ? 9.5 : 22.7,
          roasChange: platform.name.toLowerCase().includes('google') ? 14.2 : 
                  platform.name.toLowerCase().includes('meta') ? 7.8 : 19.6
        };
      });
      
      // Platformları sabit sıraya koyalım: Önce Google, sonra Meta, sonra diğerleri
      const sortedPlatforms = [...processedPlatforms].sort((a, b) => {
        if (a.name.toLowerCase().includes('google')) return -1;
        if (b.name.toLowerCase().includes('google')) return 1;
        if (a.name.toLowerCase().includes('meta')) return -1;
        if (b.name.toLowerCase().includes('meta')) return 1;
        return 0;
      });
      
      // Spend sonucunu state'e kaydedelim
      setPlatformSpendData({
        totalSpend,
        platforms: sortedPlatforms,
        // Gerçek veriye geçene kadar sahte toplam değişim değeri
        totalChange: 14.2
      });
      
      // Click sonucunu state'e kaydedelim
      setPlatformClicksData({
        totalClicks,
        platforms: sortedPlatforms, 
        // Gerçek veriye geçene kadar sahte toplam değişim değeri
        totalChange: 18.3
      });
      
      // Conversion sonucunu state'e kaydedelim
      setPlatformConversionsData({
        totalConversions,
        platforms: sortedPlatforms,
        // Gerçek veriye geçene kadar sahte toplam değişim değeri
        totalChange: 16.7
      });
      
      // ROAS sonucunu state'e kaydedelim
      setPlatformRoasData({
        totalRevenue,
        totalRoas: totalSpend > 0 ? totalRevenue / totalSpend : 0,
        platforms: sortedPlatforms,
        // Gerçek veriye geçene kadar sahte toplam değişim değeri
        totalChange: 12.5
      });
      
      console.log('Platform data prepared with consistent sorting order:', { 
        totalSpend, 
        totalClicks,
        totalConversions,
        totalRevenue,
        sortedPlatforms
      });
    }
  }, [unifiedAdPlatformData, dateRange, customStartDate, customEndDate]);

  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Ads Platforms Overview</h1>
          <p className="text-sm text-gray-500 mt-1">Comprehensive analysis of your advertising platforms</p>
        </div>
        
        {/* Date Filter Section - Compare with kısmı kaldırıldı */}
        <div className="flex items-center gap-4">
          {/* Quick Date Filters */}
          <div className="inline-flex bg-white rounded-xl shadow-[0_4px_15px_rgba(0,0,0,0.08)] p-1 border border-gray-200">
            {dateRanges.map((range) => (
              <button
                key={range.id}
                onClick={() => setDateRange(range.id)}
                className={`
                  px-4 py-2 text-sm font-medium rounded-lg transition-all
                  ${dateRange === range.id 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }
                `}
              >
                {range.label}
              </button>
            ))}
          </div>

          {/* Custom Date Button */}
          <div className="relative">
            <button 
              onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Calendar className="w-4 h-4" />
              {dateRange === 'custom' && customStartDate && customEndDate ? (
                `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
              ) : (
                'Custom Range'
              )}
            </button>
            {isDateDropdownOpen && (
              <div className="absolute right-0 mt-2 z-50">
                <DatePicker
                  selected={customStartDate}
                  onChange={handleCustomDateChange}
                  startDate={customStartDate}
                  endDate={customEndDate}
                  selectsRange
                  inline
                  className="bg-white rounded-xl shadow-lg border border-gray-200"
                />
              </div>
            )}
          </div>

          {/* Export Button */}
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-colors">
            <Download className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Platform Performance Summary Kartı */}
        <PlatformPerformanceSummary 
          dateRange={dateRange} 
          platformData={platformSpendData.platforms} 
          isLoading={unifiedAdPlatformLoading}
          dateRanges={dateRanges}
        />
        
        {/* İkinci kart - Performance Trends */}
        <Card className="rounded-2xl overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Performance Trends</h2>
                <p className="text-sm text-gray-500">Revenue and spend analysis</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Cost Chart */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-700">Cost by Platform</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-xs text-gray-600">Google Ads</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-xs text-gray-600">Meta Ads</span>
                    </div>
                  </div>
                </div>
                <div className="h-[180px]">
                  {unifiedAdPlatformLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : platformSpendData.platforms.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                      No data available for the selected period
                    </div>
                  ) : (
                    <PlatformCostChart 
                      unifiedAdPlatformData={unifiedAdPlatformData} 
                      dateRange={dateRange}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                    />
                  )}
                </div>
              </div>
              
              {/* Revenue Chart */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-700">Revenue by Platform</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-xs text-gray-600">Google Ads</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-xs text-gray-600">Meta Ads</span>
                    </div>
                  </div>
                </div>
                <div className="h-[180px]">
                  {unifiedAdPlatformLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : platformSpendData.platforms.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                      No data available for the selected period
                    </div>
                  ) : (
                    <PlatformRevenueChart 
                      unifiedAdPlatformData={unifiedAdPlatformData}
                      dateRange={dateRange}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Platform Performance Card bölümü */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Monthly breakdown by platform</h2>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
          <div className="overflow-hidden rounded-xl">
            {unifiedAdPlatformLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : platformSpendData.platforms.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-500">
                No data available for the selected period
              </div>
            ) : (
              <ExpandableTable 
                platforms={platformSpendData.platforms}
                totalSpend={platformSpendData.totalSpend}
                totalClicks={platformClicksData.totalClicks}
                totalConversions={platformConversionsData.totalConversions}
                totalRevenue={platformRoasData.totalRevenue}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function MetricCard({ title, value, change, trend, icon, color, details }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600'
  };

  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${colors[color]}`}>
          {icon}
        </div>
        <div className={`flex items-center gap-1 ${
          trend === 'up' ? 'text-green-600' : 'text-red-600'
        }`}>
          {trend === 'up' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
          <span className="text-sm font-medium">{change}</span>
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
        {details.map((detail, index) => (
          <div key={index}>
            <p className="text-xs text-gray-500">{detail.label}</p>
            <p className="text-sm font-medium text-gray-800">{detail.value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BudgetRow({ platform, spent, total, color }) {
  const percentage = (spent / total) * 100;
  const colors = {
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500'
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{platform}</span>
        <span className="text-sm font-medium text-gray-800">₺{spent.toLocaleString()}</span>
        </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colors[color]} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">{percentage.toFixed(1)}% spent</span>
        <span className="text-gray-500">₺{total.toLocaleString()} budget</span>
      </div>
    </div>
  );
}

function PlatformRow({ platform, icon, color, data }) {
  const statusColors = {
    'Optimized': 'bg-green-50 text-green-600',
    'Learning': 'bg-blue-50 text-blue-600',
    'Need Attention': 'bg-red-50 text-red-600'
  };

  return (
    <tr className="hover:bg-gray-50/50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg bg-${color}-100`}>
            <img src={icon} className="w-4 h-4" alt={platform} />
          </div>
          <span className="font-medium text-gray-800">{platform}</span>
        </div>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="font-medium text-gray-800">{data.spend.toLocaleString()}</span>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="font-medium text-gray-800">{data.clicks.toLocaleString()}</span>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="font-medium text-gray-800">{data.cpc.toFixed(2)}</span>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="font-medium text-gray-800">{data.conversions.toLocaleString()}</span>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="font-medium text-gray-800">{data.cpa.toFixed(2)}</span>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="font-medium text-gray-800">{data.revenue.toLocaleString()}</span>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="font-medium text-gray-800">{data.roas.toFixed(1)}x</span>
      </td>
      <td className="py-4 px-4 text-right">
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[data.status]}`}>
          {data.status}
        </span>
      </td>
    </tr>
  );
}

function LineChart({ data, metric }) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'white',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        bodyFont: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        titleFont: {
          size: 12,
          family: "'Inter', sans-serif",
          weight: '600',
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              switch (metric) {
                case 'spend':
                  return label + new Intl.NumberFormat('en-US', { 
                    style: 'currency', 
                    currency: 'TRY',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(context.parsed.y);
                case 'roas':
                  return label + context.parsed.y.toFixed(1) + 'x';
                case 'conversions':
                  return label + context.parsed.y.toLocaleString('en-US');
                case 'cpc':
                  return label + new Intl.NumberFormat('en-US', { 
                    style: 'currency', 
                    currency: 'TRY',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(context.parsed.y);
                default:
                  return label + context.parsed.y;
              }
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            family: "'Inter', sans-serif",
          },
          color: '#6b7280',
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
      y: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            size: 12,
            family: "'Inter', sans-serif",
          },
          color: '#6b7280',
          callback: function(value) {
            switch (metric) {
              case 'spend':
                return new Intl.NumberFormat('tr-TR', { 
                  style: 'currency', 
                  currency: 'TRY',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(value);
              case 'roas':
                return value.toFixed(1) + 'x';
              case 'conversions':
                return value.toLocaleString();
              case 'cpc':
                return new Intl.NumberFormat('tr-TR', { 
                  style: 'currency', 
                  currency: 'TRY',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(value);
              default:
                return value;
            }
          }
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
  };

  return <Line options={options} data={data} />;
} 

// LineChart fonksiyonunun üstüne şu yeni fonksiyonu ekleyelim:

function CombinedLineChart({ googleData, metaData, dateRange }) {
  // Günlük tarih dizisi oluştur (son 30 gün veya seçilen aralık)
  const getDates = () => {
    const today = new Date();
    let days = 30; // Varsayılan olarak 30 gün
    
    switch(dateRange) {
      case 'yesterday':
        days = 1;
        break;
      case '7days':
        days = 7;
        break;
      case '30days':
        days = 30;
        break;
      case 'this_month':
        // Ayın başından bugüne kadar
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        days = Math.ceil((today - startOfMonth) / (1000 * 60 * 60 * 24));
        break;
      default:
        days = 30;
    }
    
    return Array.from({length: days}, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1) + i);
      return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    });
  };
  
  // Chart data oluştur - çoklu metrik ekleyelim (cost ve revenue)
  const chartData = {
    labels: getDates(),
    datasets: [
      // Google Ads Cost
      {
        label: 'Google Ads - Cost',
        data: Array.from({length: getDates().length}, () => 
          googleData ? Math.floor(googleData.spend / getDates().length * (0.8 + Math.random() * 0.4)) : 0
        ),
        borderColor: 'rgb(239, 68, 68)', // Google kırmızı
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        yAxisID: 'y',
      },
      // Google Ads Revenue
      {
        label: 'Google Ads - Revenue',
        data: Array.from({length: getDates().length}, () => 
          googleData ? Math.floor(googleData.conversionValue / getDates().length * (0.8 + Math.random() * 0.4)) : 0
        ),
        borderColor: 'rgb(220, 38, 38)', // Koyu kırmızı - revenue için
        borderDash: [5, 5], // Kesikli çizgi ile revenue'yu ayırt edelim
        backgroundColor: 'rgba(220, 38, 38, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        yAxisID: 'y',
      },
      // Meta Ads Cost
      {
        label: 'Meta Ads - Cost',
        data: Array.from({length: getDates().length}, () => 
          metaData ? Math.floor(metaData.spend / getDates().length * (0.8 + Math.random() * 0.4)) : 0
        ),
        borderColor: 'rgb(37, 99, 235)', // Meta mavi
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        yAxisID: 'y',
      },
      // Meta Ads Revenue
      {
        label: 'Meta Ads - Revenue',
        data: Array.from({length: getDates().length}, () => 
          metaData ? Math.floor(metaData.conversionValue / getDates().length * (0.8 + Math.random() * 0.4)) : 0
        ),
        borderColor: 'rgb(30, 64, 175)', // Koyu mavi - revenue için
        borderDash: [5, 5], // Kesikli çizgi ile revenue'yu ayırt edelim
        backgroundColor: 'rgba(30, 64, 175, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        yAxisID: 'y',
      }
    ]
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true, // Legend'i göster (çoklu metrik olduğu için)
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 6,
          font: {
            size: 11,
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'white',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        bodyFont: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        titleFont: {
          size: 12,
          family: "'Inter', sans-serif",
          weight: '600',
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              return label + new Intl.NumberFormat('en-US', { 
                style: 'currency', 
                currency: 'TRY',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            family: "'Inter', sans-serif",
          },
          color: '#6b7280',
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
      y: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
          color: '#9ca3af',
          callback: function(value) {
            return new Intl.NumberFormat('en-US', { 
              style: 'currency', 
              currency: 'TRY',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          }
        },
        beginAtZero: true,
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
  };

  return <Line options={options} data={chartData} />;
}

function ExpandableTable({ platforms, totalSpend, totalClicks, totalConversions, totalRevenue }) {
  const [expandedPlatforms, setExpandedPlatforms] = useState([]);
  const [allTimeData, setAllTimeData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Komponent ilk yüklendiğinde Google Ads ve Meta Ads'ı açık göster
  useEffect(() => {
    if (platforms.length > 0) {
      const platformsToExpand = platforms
        .filter(p => 
          p.name.toLowerCase().includes('google') || 
          p.name.toLowerCase().includes('meta')
        )
        .map(p => p.name);
      
      setExpandedPlatforms(platformsToExpand);
      
      // Tüm zamanların verisini al
      fetchAllTimeData();
    }
  }, [platforms]);
  
  // Filtreden bağımsız olarak tüm zamanların verisini al
  const fetchAllTimeData = async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      
      // Build URL with custom date parameters if needed  
      let apiUrl = `https://api.glowytics.com/api/google-ads/unified-ad-platform-daily-summary?dateRange=all`; // ExpandableTable hep all data kullanır
      
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch all-time data');
      }
      
      const result = await response.json();
      console.log('All-time data fetched for platform table:', result.length, 'records');
      
      // Platform bazında verileri gruplayalım
      const platformData = {};
      
      result.forEach(item => {
        const sourceName = item.source || 'Unknown';
        
        if (!platformData[sourceName]) {
          platformData[sourceName] = {
            name: sourceName,
            monthlyData: {},
            // Toplam değerler için yeni alanlar ekleyelim
            totalSpend: 0,
            totalClicks: 0,
            totalImpressions: 0,
            totalConversions: 0,
            totalConversionValue: 0
          };
        }
        
        // Tarih bilgisinden yıl ve ay al
        const date = new Date(item.date);
        const month = date.toLocaleString('en-US', { month: 'long' });
        const yearMonth = `${date.getFullYear()}-${date.getMonth() + 1}`;
        
        if (!platformData[sourceName].monthlyData[yearMonth]) {
          platformData[sourceName].monthlyData[yearMonth] = {
            year: date.getFullYear(),
            month: month,
            spend: 0,
            clicks: 0,
            impressions: 0,
            conversions: 0,
            conversionValue: 0
          };
        }
        
        // Bu ay için değerleri topla
        platformData[sourceName].monthlyData[yearMonth].spend += Number(item.totalCost) || 0;
        platformData[sourceName].monthlyData[yearMonth].clicks += Number(item.totalClicks) || 0;
        platformData[sourceName].monthlyData[yearMonth].impressions += Number(item.totalImpressions) || 0;
        platformData[sourceName].monthlyData[yearMonth].conversions += Number(item.totalConversions) || 0;
        platformData[sourceName].monthlyData[yearMonth].conversionValue += Number(item.totalConversionValue) || 0;
        
        // Platform toplam değerlerini de güncelle
        platformData[sourceName].totalSpend += Number(item.totalCost) || 0;
        platformData[sourceName].totalClicks += Number(item.totalClicks) || 0;
        platformData[sourceName].totalImpressions += Number(item.totalImpressions) || 0;
        platformData[sourceName].totalConversions += Number(item.totalConversions) || 0;
        platformData[sourceName].totalConversionValue += Number(item.totalConversionValue) || 0;
      });
      
      // Şimdi her platform için ayları ters sıralayalım (en son ay en üstte)
      Object.keys(platformData).forEach(platform => {
        const months = Object.entries(platformData[platform].monthlyData).map(([key, data]) => ({
          ...data,
          yearMonth: key
        }));
        
        // Aylık verileri sırala (en yeni ay en üstte)
        const sortedMonths = months.sort((a, b) => {
          return b.yearMonth.localeCompare(a.yearMonth);
        });
        
        // Her ay için hesaplamaları yap
        const processedMonths = sortedMonths.map(month => ({
          ...month,
          cpc: month.clicks > 0 ? month.spend / month.clicks : 0,
          cpa: month.conversions > 0 ? month.spend / month.conversions : 0,
          cr: month.clicks > 0 ? (month.conversions / month.clicks) * 100 : 0, // CR (Conversion Rate) hesaplama
          roas: month.spend > 0 ? month.conversionValue / month.spend : 0
        }));
        
        // Ayları diziye dönüştür
        platformData[platform].monthlyData = processedMonths;
        
        // Platform ve toplam değerler için yeni hesaplamalar
        const platformTotals = platformData[platform];
        platformTotals.cpc = platformTotals.totalClicks > 0 ? platformTotals.totalSpend / platformTotals.totalClicks : 0;
        platformTotals.cpa = platformTotals.totalConversions > 0 ? platformTotals.totalSpend / platformTotals.totalConversions : 0;
        platformTotals.cr = platformTotals.totalClicks > 0 ? (platformTotals.totalConversions / platformTotals.totalClicks) * 100 : 0;
        platformTotals.roas = platformTotals.totalSpend > 0 ? platformTotals.totalConversionValue / platformTotals.totalSpend : 0;
      });
      
      // Tüm platformların toplam değerlerini hesapla
      const allPlatformsTotals = {
        totalSpend: Object.values(platformData).reduce((sum, p) => sum + p.totalSpend, 0),
        totalClicks: Object.values(platformData).reduce((sum, p) => sum + p.totalClicks, 0),
        totalConversions: Object.values(platformData).reduce((sum, p) => sum + p.totalConversions, 0),
        totalConversionValue: Object.values(platformData).reduce((sum, p) => sum + p.totalConversionValue, 0)
      };
      
      console.log('All platforms totals calculated from monthly data:', allPlatformsTotals);
      
      setAllTimeData(platformData);
      
    } catch (error) {
      console.error('Error fetching all-time data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Platform adına tıklandığında o platformun detaylarını göster/gizle
  const togglePlatform = (platformName) => {
    if (expandedPlatforms.includes(platformName)) {
      setExpandedPlatforms(expandedPlatforms.filter(p => p !== platformName));
    } else {
      setExpandedPlatforms([...expandedPlatforms, platformName]);
    }
  };
  
  // Platform açık mı kontrol et
  const isExpanded = (platformName) => {
    return expandedPlatforms.includes(platformName);
  };
  
  // Değişim yüzdesi hesapla (önceki aya göre)
  const calculateChange = (currentValue, previousValue) => {
    if (!previousValue || previousValue === 0) return null;
    return ((currentValue - previousValue) / previousValue) * 100;
  };
  
  // Aylık verileri toplamak için yardımcı fonksiyon
  const sumMonthlyData = (platform) => {
    if (!allTimeData || !allTimeData[platform.name]) {
      return {
        spend: platform.spend || 0,
        clicks: platform.clicks || 0,
        conversions: platform.conversions || 0,
        conversionValue: platform.conversionValue || 0,
        cpc: platform.clicks > 0 ? platform.spend / platform.clicks : 0,
        cpa: platform.conversions > 0 ? platform.spend / platform.conversions : 0,
        cr: platform.clicks > 0 ? (platform.conversions / platform.clicks) * 100 : 0,
        roas: platform.spend > 0 ? platform.conversionValue / platform.spend : 0
      };
    }
    
    const platformData = allTimeData[platform.name];
    return {
      spend: platformData.totalSpend || 0,
      clicks: platformData.totalClicks || 0,
      conversions: platformData.totalConversions || 0,
      conversionValue: platformData.totalConversionValue || 0,
      cpc: platformData.cpc || 0,
      cpa: platformData.cpa || 0,
      cr: platformData.cr || 0,
      roas: platformData.roas || 0
    };
  };
  
  return (
    <div className="rounded-xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-blue-50 border-b border-blue-100">
            <th className="text-left py-3 px-4 text-xs font-medium text-blue-600 uppercase rounded-tl-xl">Platform</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">Spend</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">Clicks</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">CPC</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">Conv.</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">CR</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">CPA</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">Revenue</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase">ROAS</th>
            <th className="text-right py-3 px-4 text-xs font-medium text-blue-600 uppercase rounded-tr-xl">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {platforms.map((platform, index) => {
            // Her platform için aylık verileri topla
            const platformTotals = sumMonthlyData(platform);
            
            return (
              <React.Fragment key={index}>
                <tr className={`hover:bg-gray-50/50 transition-colors ${isExpanded(platform.name) ? 'bg-blue-50/50' : ''}`}>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => togglePlatform(platform.name)}
                        className="flex items-center gap-3"
                      >
                        <div className={`p-1.5 rounded-lg ${
                          platform.name.toLowerCase().includes('google') ? 'bg-red-100' : 
                          platform.name.toLowerCase().includes('meta') ? 'bg-blue-100' : 
                          'bg-purple-100'
                        }`}>
                          <img 
                            src={
                              platform.name.toLowerCase().includes('google') ? "/google_ads-512.webp" : 
                              platform.name.toLowerCase().includes('meta') ? "/6033716.png" : 
                              "/tiktok-icon.png"
                            } 
                            className="w-4 h-4" 
                            alt={platform.name} 
                          />
                        </div>
                        <span className="font-medium text-gray-800">
                          {platform.name.toLowerCase().includes('google') ? 'Google Ads' : 
                          platform.name.toLowerCase().includes('meta') ? 'Meta Ads' : 
                          platform.name.toLowerCase().includes('tiktok') ? 'TikTok Ads' :
                          platform.name}
                        </span>
                        {isExpanded(platform.name) ? 
                          <ChevronRight className="w-4 h-4 text-gray-400 transform rotate-90" /> : 
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        }
                      </button>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">{formatCurrency(platformTotals.spend)}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">{formatNumber(platformTotals.clicks)}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">{formatCurrency(platformTotals.cpc)}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">{formatNumber(platformTotals.conversions)}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">
                      {platformTotals.cr !== undefined && platformTotals.cr !== null ? platformTotals.cr.toFixed(2) : '0.00'}%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">{formatCurrency(platformTotals.cpa)}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">{formatCurrency(platformTotals.conversionValue)}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-gray-800">{(platformTotals.roas !== undefined && platformTotals.roas !== null) ? platformTotals.roas.toFixed(1) : '0.0'}x</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      platformTotals.roas > 4 ? 'bg-green-50 text-green-600' : 
                      platformTotals.roas > 2 ? 'bg-blue-50 text-blue-600' : 
                      'bg-red-50 text-red-600'
                    }`}>
                      {platformTotals.roas > 4 ? 'Optimized' : 
                       platformTotals.roas > 2 ? 'Learning' : 
                       'Need Attention'}
                    </span>
                  </td>
                </tr>
                
                {/* Genişletilmiş aylık kırılım - başlık olmadan */}
                {isExpanded(platform.name) && (
                  <tr>
                    <td colSpan="10" className="bg-gray-50 p-4">
                      <div className="overflow-x-auto">
                        {loading ? (
                          <div className="flex justify-center py-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : !allTimeData || !allTimeData[platform.name] ? (
                          <div className="text-center py-2 text-gray-500 text-sm">
                            Data could not be loaded. Please try again later.
                          </div>
                        ) : (
                          <table className="w-full border-separate border-spacing-0 rounded-lg overflow-hidden">
                            <tbody>
                              {allTimeData[platform.name].monthlyData.map((month, idx) => {
                                // Bir sonraki ay (daha eski ay)
                                const nextMonth = idx < allTimeData[platform.name].monthlyData.length - 1 
                                  ? allTimeData[platform.name].monthlyData[idx + 1] 
                                  : null;

                                // Değişim yüzdeleri
                                const spendChange = calculateChange(month.spend, nextMonth?.spend);
                                
                                return (
                                  <tr key={idx} className="text-xs hover:bg-gray-100/70">
                                    <td className="py-3 px-3 font-medium text-gray-700 border-b border-gray-200">
                                      {month.month} {month.year}
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {formatCurrency(month.spend)}
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {formatNumber(month.clicks)}
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {formatCurrency(month.cpc)}
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {formatNumber(month.conversions)}
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {(month.cr !== undefined && month.cr !== null) ? month.cr.toFixed(2) : '0.00'}%
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {formatCurrency(month.cpa)}
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {formatCurrency(month.conversionValue)}
                                    </td>
                                    <td className="py-3 px-3 text-right text-gray-700 border-b border-gray-200">
                                      {(month.roas !== undefined && month.roas !== null) ? month.roas.toFixed(1) : '0.0'}x
                                    </td>
                                    <td className="py-3 px-3 text-right border-b border-gray-200">
                                      {spendChange !== null ? (
                                        <span className={`text-xs flex items-center justify-end gap-1 ${spendChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {spendChange >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                                          {Math.abs(spendChange).toFixed(1)}%
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400">-</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          
          {/* Toplam satırı */}
          <tr className="bg-gray-50 font-medium">
            <td className="py-4 px-4 rounded-bl-xl">
              <span className="font-semibold text-gray-800">TOTAL</span>
            </td>
            <td className="py-4 px-4 text-right">
              {/* Burada artık platformlardan toplanan toplam değerleri kullanıyoruz */}
              <span className="font-semibold text-gray-800">
                {formatCurrency(platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.spend;
                }, 0))}
              </span>
            </td>
            <td className="py-4 px-4 text-right">
              <span className="font-semibold text-gray-800">
                {formatNumber(platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.clicks;
                }, 0))}
              </span>
            </td>
            <td className="py-4 px-4 text-right">
              <span className="font-semibold text-gray-800">
                {formatCurrency(
                  (platforms.reduce((sum, p) => {
                    const platformTotals = sumMonthlyData(p);
                    return sum + platformTotals.spend;
                  }, 0)) / 
                  (platforms.reduce((sum, p) => {
                    const platformTotals = sumMonthlyData(p);
                    return sum + platformTotals.clicks;
                  }, 0))
                )}
              </span>
            </td>
            <td className="py-4 px-4 text-right">
              <span className="font-semibold text-gray-800">
                {formatNumber(platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.conversions;
                }, 0))}
              </span>
            </td>
            <td className="py-4 px-4 text-right">
              <span className="font-semibold text-gray-800">
                {((platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.conversions;
                }, 0)) / 
                (platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.clicks;
                }, 0)) * 100 || 0).toFixed(2)}%
              </span>
            </td>
            <td className="py-4 px-4 text-right">
              <span className="font-semibold text-gray-800">
                {formatCurrency(
                  (platforms.reduce((sum, p) => {
                    const platformTotals = sumMonthlyData(p);
                    return sum + platformTotals.spend;
                  }, 0)) / 
                  (platforms.reduce((sum, p) => {
                    const platformTotals = sumMonthlyData(p);
                    return sum + platformTotals.conversions;
                  }, 0))
                )}
              </span>
            </td>
            <td className="py-4 px-4 text-right">
              <span className="font-semibold text-gray-800">
                {formatCurrency(platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.conversionValue;
                }, 0))}
              </span>
            </td>
            <td className="py-4 px-4 text-right">
              <span className="font-semibold text-gray-800">
                {(
                  (platforms.reduce((sum, p) => {
                    const platformTotals = sumMonthlyData(p);
                    return sum + platformTotals.conversionValue;
                  }, 0)) / 
                  (platforms.reduce((sum, p) => {
                    const platformTotals = sumMonthlyData(p);
                    return sum + platformTotals.spend;
                  }, 0)) || 0
                ).toFixed(1)}x
              </span>
            </td>
            <td className="py-4 px-4 text-right rounded-br-xl">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                (platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.conversionValue;
                }, 0)) / 
                (platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.spend;
                }, 0)) > 4 ? 'bg-green-50 text-green-600' : 
                (platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.conversionValue;
                }, 0)) / 
                (platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.spend;
                }, 0)) > 2 ? 'bg-blue-50 text-blue-600' : 
                'bg-red-50 text-red-600'
              }`}>
                {(platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.conversionValue;
                }, 0)) / 
                (platforms.reduce((sum, p) => {
                  const platformTotals = sumMonthlyData(p);
                  return sum + platformTotals.spend;
                }, 0)) > 4 ? 'Optimized' : 
                 (platforms.reduce((sum, p) => {
                   const platformTotals = sumMonthlyData(p);
                   return sum + platformTotals.conversionValue;
                 }, 0)) / 
                 (platforms.reduce((sum, p) => {
                   const platformTotals = sumMonthlyData(p);
                   return sum + platformTotals.spend;
                 }, 0)) > 2 ? 'Good' : 
                 'Needs Improvement'}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Platform Performance Summary kardı için güncellenen komponent
function PlatformPerformanceSummary({ dateRange, platformData, isLoading, dateRanges }) {
  return (
    <Card className="rounded-2xl overflow-hidden bg-white shadow-md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-800">Platform Performance Summary</h2>
          <div className="text-sm text-gray-500">
            {dateRanges?.find(r => r.id === dateRange)?.label || 'Custom Range'}
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : !platformData || platformData.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-gray-500">
            No data available for the selected period
          </div>
        ) : (
          <div className="space-y-8">
            {/* Platform bazında metrikler */}
            {platformData.map((platform, index) => (
              <div key={index} className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm">
                <div className="flex justify-between items-start mb-5">
                  {/* Platform bilgisi ve ROAS*/}
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${
                      platform.name.toLowerCase().includes('google') ? 'bg-red-100' : 
                      platform.name.toLowerCase().includes('meta') ? 'bg-blue-100' : 
                      'bg-purple-100'
                    }`}>
                      <img 
                        src={
                          platform.name.toLowerCase().includes('google') ? "/google_ads-512.webp" : 
                          platform.name.toLowerCase().includes('meta') ? "/6033716.png" : 
                          "/tiktok-icon.png"
                        } 
                        className="w-6 h-6" 
                        alt={platform.name} 
                      />
                    </div>
                    <div>
                      <div className="font-medium text-gray-800 text-lg mb-1">
                        {platform.name.toLowerCase().includes('google') ? 'Google Ads' : 
                        platform.name.toLowerCase().includes('meta') ? 'Meta Ads' : 
                        platform.name.toLowerCase().includes('tiktok') ? 'TikTok Ads' :
                        platform.name}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Revenue:</span>
                        <span className="text-sm font-medium text-green-600">{formatCurrencyWhole(platform.conversionValue)}</span>
                        <span className="text-xs font-medium text-gray-600 ml-2">ROAS: {platform.roas.toFixed(1)}x</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Harcama değeri - daha küçük font */}
                  <div className="text-right">
                    <div className="font-semibold text-lg">{formatCurrencyWhole(platform.spend)}</div>
                    <div className="text-xs text-gray-500 mt-1">Cost</div>
                  </div>
                </div>
                
                {/* Metrikler yeni düzen - renkli kartlar, daha küçük fontlar */}
                <div className="grid grid-cols-6 gap-6 mt-6">
                  {/* Impressions */}
                  <div className="bg-indigo-50 p-3 rounded-lg">
                    <div className="text-xs text-indigo-600 mb-1">Impr.</div>
                    <div className="font-medium text-sm text-indigo-800">{formatNumber(platform.impressions)}</div>
                  </div>
                  
                  {/* Clicks */}
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <div className="text-xs text-yellow-600 mb-1">Clicks</div>
                    <div className="font-medium text-sm text-yellow-800">{formatNumber(platform.clicks)}</div>
                  </div>
                  
                  {/* CPC */}
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-xs text-blue-600 mb-1">CPC</div>
                    <div className="font-medium text-sm text-blue-700">{formatCurrency(platform.cpc)}</div>
                  </div>
                  
                  {/* Conversions */}
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-xs text-green-600 mb-1">Conv.</div>
                    <div className="font-medium text-sm text-green-800">{formatNumber(platform.conversions)}</div>
                  </div>
                  
                  {/* CR (Conversion Rate) */}
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="text-xs text-purple-600 mb-1">CR</div>
                    <div className="font-medium text-sm text-purple-800">
                      {(platform.clicks > 0 ? (platform.conversions / platform.clicks * 100) : 0).toFixed(2)}%
                    </div>
                  </div>
                  
                  {/* CPA */}
                  <div className="bg-red-50 p-3 rounded-lg">
                    <div className="text-xs text-red-600 mb-1">CPA</div>
                    <div className="font-medium text-sm text-red-700">{formatCurrency(platform.cpa)}</div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Toplam değerler - büyük sayıları daha küçük yapalım */}
            <div className="grid grid-cols-3 gap-4 pt-4 mt-2 border-t border-blue-100">
              {/* Total Cost */}
              <div className="bg-blue-50 p-4 rounded-xl">
                <div className="text-sm text-blue-700 font-medium">Total Spend</div>
                <div className="text-xl font-bold text-blue-900">{formatCurrencyWhole(platformData.reduce((sum, p) => sum + p.spend, 0))}</div>
              </div>
              
              {/* Total Clicks */}
              <div className="bg-yellow-50 p-4 rounded-xl">
                <div className="text-sm text-yellow-700 font-medium">Total Clicks</div>
                <div className="text-xl font-bold text-yellow-900">{formatNumber(platformData.reduce((sum, p) => sum + p.clicks, 0))}</div>
              </div>
              
              {/* Total Revenue */}
              <div className="bg-green-50 p-4 rounded-xl">
                <div className="text-sm text-green-700 font-medium">Total Revenue</div>
                <div className="text-xl font-bold text-green-800">{formatCurrencyWhole(platformData.reduce((sum, p) => sum + p.conversionValue, 0))}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// Cost (Harcama) için line chart
function PlatformCostChart({ unifiedAdPlatformData, dateRange, customStartDate, customEndDate }) {
  // Günlük tarih dizisi oluştur (seçilen aralığa göre)
  const getDates = () => {
    const today = new Date();
    
    // Custom date range için
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const dates = [];
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
      }
      return dates;
    }
    
    // Diğer date range'ler için
    let days = 30; // Varsayılan olarak 30 gün
    
    switch(dateRange) {
      case 'yesterday':
        days = 1;
        break;
      case '7days':
        days = 7;
        break;
      case '30days':
        days = 30;
        break;
      case 'this_month':
        // Ayın başından bugüne kadar
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        days = Math.ceil((today - startOfMonth) / (1000 * 60 * 60 * 24));
        break;
      default:
        days = 30;
    }
    
    return Array.from({length: days}, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1) + i);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    });
  };

  const labels = getDates();

  const getPlatformDailyData = (platformName) => {
    const platformData = unifiedAdPlatformData?.filter(d => d.source.toLowerCase().includes(platformName)) || [];
    const dailyMap = new Map();
    labels.forEach(label => {
      const matchingData = platformData.find(d => new Date(d.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) === label);
      dailyMap.set(label, matchingData ? matchingData.totalCost : 0);
    });
    return Array.from(dailyMap.values());
  };
  
  // Chart data oluştur - sadece cost verileri
  const chartData = {
    labels: labels,
    datasets: [
      // Google Ads Cost - renkler aynı kalsın
      {
        label: 'Google Ads',
        data: getPlatformDailyData('google'),
        borderColor: 'rgb(239, 68, 68)', // Google kırmızı
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 1.5,
        pointHoverRadius: 4,
        yAxisID: 'y',
      },
      // Meta Ads Cost
      {
        label: 'Meta Ads',
        data: getPlatformDailyData('meta'),
        borderColor: 'rgb(37, 99, 235)', // Meta mavi
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 1.5,
        pointHoverRadius: 4,
        yAxisID: 'y',
      }
    ]
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Legend'i gizle, kendi custom legend ekledik
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'white',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 8,
        bodyFont: {
          size: 11,
          family: "'Inter', sans-serif",
        },
        titleFont: {
          size: 11,
          family: "'Inter', sans-serif",
          weight: '600',
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              return label + new Intl.NumberFormat('en-US', { 
                style: 'currency', 
                currency: 'TRY',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
          color: '#9ca3af',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6,
        },
      },
      y: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
          color: '#9ca3af',
          callback: function(value) {
            return new Intl.NumberFormat('en-US', { 
              style: 'currency', 
              currency: 'TRY',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          }
        },
        beginAtZero: true,
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
  };

  return <Line options={options} data={chartData} />;
}

// Revenue (Gelir) için line chart
function PlatformRevenueChart({ unifiedAdPlatformData, dateRange, customStartDate, customEndDate }) {
  // Günlük tarih dizisi oluştur (seçilen aralığa göre)
  const getDates = () => {
    const today = new Date();
    
    // Custom date range için
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const dates = [];
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
      }
      return dates;
    }
    
    // Diğer date range'ler için
    let days = 30; // Varsayılan olarak 30 gün
    
    switch(dateRange) {
      case 'yesterday':
        days = 1;
        break;
      case '7days':
        days = 7;
        break;
      case '30days':
        days = 30;
        break;
      case 'this_month':
        // Ayın başından bugüne kadar
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        days = Math.ceil((today - startOfMonth) / (1000 * 60 * 60 * 24));
        break;
      default:
        days = 30;
    }
    
    return Array.from({length: days}, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1) + i);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    });
  };
  
  const labels = getDates();

  const getPlatformDailyRevenue = (platformName) => {
    const platformData = unifiedAdPlatformData?.filter(d => d.source.toLowerCase().includes(platformName)) || [];
    const dailyMap = new Map();
    labels.forEach(label => {
      const matchingData = platformData.find(d => new Date(d.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) === label);
      dailyMap.set(label, matchingData ? matchingData.totalConversionValue : 0);
    });
    return Array.from(dailyMap.values());
  };

  // Chart data oluştur - sadece revenue verileri
  const chartData = {
    labels: labels,
    datasets: [
      // Google Ads Revenue - Renkleri Google Ads ile tutarlı yapalım (kırmızı)
      {
        label: 'Google Ads',
        data: getPlatformDailyRevenue('google'),
        borderColor: 'rgb(239, 68, 68)', // Google için kırmızı (cost chart ile aynı)
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 1.5,
        pointHoverRadius: 4,
        yAxisID: 'y',
      },
      // Meta Ads Revenue - Renkleri Meta Ads ile tutarlı yapalım (mavi)
      {
        label: 'Meta Ads',
        data: getPlatformDailyRevenue('meta'),
        borderColor: 'rgb(37, 99, 235)', // Meta için mavi (cost chart ile aynı)
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 1.5,
        pointHoverRadius: 4,
        yAxisID: 'y',
      }
    ]
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Legend'i gizle, kendi custom legend ekledik
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'white',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 8,
        bodyFont: {
          size: 11,
          family: "'Inter', sans-serif",
        },
        titleFont: {
          size: 11,
          family: "'Inter', sans-serif",
          weight: '600',
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              return label + new Intl.NumberFormat('en-US', { 
                style: 'currency', 
                currency: 'TRY',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10, // Cost chart ile aynı boyut (12 yerine 10)
            family: "'Inter', sans-serif",
          },
          color: '#9ca3af', // Cost chart ile aynı renk (6b7280 yerine 9ca3af)
          maxRotation: 0, // Cost chart ile aynı rotasyon (45 değil 0)
          minRotation: 0, // Cost chart ile aynı rotasyon (45 değil 0)
          autoSkip: true,
          maxTicksLimit: 6, // Cost chart ile aynı limit (10 değil 6)
        },
      },
      y: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            size: 10,
            family: "'Inter', sans-serif",
          },
          color: '#9ca3af',
          callback: function(value) {
            return new Intl.NumberFormat('en-US', { 
              style: 'currency', 
              currency: 'TRY',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          }
        },
        beginAtZero: true,
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
  };

  return <Line options={options} data={chartData} />;
}